import { BlockType, type LevelBounds, type Vector3D } from '../types';
import type { WorldManager } from '../world/WorldManager';

export interface MinimapMarker {
  x: number;
  z: number;
  kind: 'player' | 'remote' | 'enemy' | 'exit' | 'shop' | 'entrance';
  yaw?: number;
  label?: string;
}

export interface MinimapFrame {
  stage: number;
  level: LevelBounds | null;
  player: Vector3D;
  yaw: number;
  markers: MinimapMarker[];
  /** World-unit reveal radius around the player. */
  revealRadius?: number;
}

/** Per-column classification for the top-down map. */
const enum CellKind {
  Rock = 1,
  Tunnel = 2,
  Ore = 3,
  Exit = 4,
  Shop = 5,
}

/**
 * Exploration minimap: fog of war fills in as you walk,
 * tunnels read as bright paths against dark rock.
 * Terrain is a pixel map; the player cursor is drawn on a sharp overlay.
 */
export class MinimapUI {
  private readonly root: HTMLDivElement;
  private readonly terrain: HTMLCanvasElement;
  private readonly overlay: HTMLCanvasElement;
  private readonly tctx: CanvasRenderingContext2D;
  private readonly octx: CanvasRenderingContext2D;
  private readonly label: HTMLDivElement;
  private readonly displaySize: number;

  private mapW = 0;
  private mapD = 0;
  private cells: Uint8Array | null = null;
  private revealed: Uint8Array | null = null;
  private stageKey = '';
  private cellsDirty = false;
  private terrainDirty = true;
  private visible = true;
  private lastPaintAt = 0;

  constructor(parent: HTMLElement, displaySize = 180) {
    this.displaySize = displaySize;
    this.root = document.createElement('div');
    this.root.id = 'minimap';
    this.root.innerHTML = `
      <canvas class="minimap-terrain" aria-hidden="true"></canvas>
      <canvas class="minimap-overlay" aria-label="Minimap"></canvas>
      <div class="minimap-label"></div>
    `;
    parent.appendChild(this.root);
    this.terrain = this.root.querySelector('.minimap-terrain') as HTMLCanvasElement;
    this.overlay = this.root.querySelector('.minimap-overlay') as HTMLCanvasElement;
    const tctx = this.terrain.getContext('2d', { alpha: false });
    const octx = this.overlay.getContext('2d', { alpha: true });
    if (!tctx || !octx) throw new Error('2d canvas unavailable for minimap');
    this.tctx = tctx;
    this.octx = octx;
    this.tctx.imageSmoothingEnabled = false;
    this.octx.imageSmoothingEnabled = true;
    this.label = this.root.querySelector('.minimap-label') as HTMLDivElement;
    this.overlay.width = displaySize;
    this.overlay.height = displaySize;
    this.injectStyles();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.root.style.display = visible ? 'block' : 'none';
  }

  dispose(): void {
    this.root.remove();
  }

  /** Clear fog + terrain (new stage / world regen). */
  resetExploration(): void {
    this.stageKey = '';
    this.cells = null;
    this.revealed = null;
    this.cellsDirty = false;
    this.terrainDirty = true;
    this.lastPaintAt = 0;
  }

  /** Keep fog; reclassify terrain after digs. */
  invalidate(): void {
    if (this.cells) {
      this.cellsDirty = true;
      this.terrainDirty = true;
    } else {
      this.resetExploration();
    }
  }

  /**
   * @param maxHz Cap how often ImageData terrain + overlay redraw. Overlay still
   * updates when the throttle allows; revelation marks terrain dirty cheaply.
   */
  render(world: WorldManager, frame: MinimapFrame, maxHz = 20): void {
    if (!this.visible) return;
    const level = frame.level;
    if (!level) {
      this.ensureTerrain(48, 48);
      this.tctx.fillStyle = '#05080c';
      this.tctx.fillRect(0, 0, this.terrain.width, this.terrain.height);
      this.octx.clearRect(0, 0, this.displaySize, this.displaySize);
      this.label.textContent = 'No map';
      return;
    }

    const key = `${world.getSeed()}:${world.getRunId()}:${frame.stage}:${level.floorY}`;
    if (this.stageKey !== key || !this.cells || !this.revealed) {
      this.mapW = world.width;
      this.mapD = world.depth;
      this.ensureTerrain(this.mapW, this.mapD);
      this.cells = new Uint8Array(this.mapW * this.mapD);
      this.revealed = new Uint8Array(this.mapW * this.mapD);
      this.classifyLevel(world, level);
      this.cellsDirty = false;
      this.terrainDirty = true;
      this.stageKey = key;
    } else if (this.cellsDirty) {
      this.classifyLevel(world, level);
      this.cellsDirty = false;
      this.terrainDirty = true;
    }

    const revealedNew = this.revealAround(frame.player, world.blockSize, frame.revealRadius ?? 8);
    if (revealedNew) this.terrainDirty = true;

    const now = performance.now();
    const minInterval = 1000 / Math.max(1, maxHz);
    // Terrain changes paint immediately; otherwise throttle overlay redraws.
    if (!this.terrainDirty && now - this.lastPaintAt < minInterval) return;
    this.lastPaintAt = now;

    if (this.terrainDirty) {
      this.paintTerrain();
      this.terrainDirty = false;
    }
    this.paintOverlay(world, frame);
    this.label.textContent = `Stage ${frame.stage}`;
  }

  private ensureTerrain(w: number, d: number): void {
    if (this.terrain.width !== w || this.terrain.height !== d) {
      this.terrain.width = w;
      this.terrain.height = d;
    }
  }

  private classifyLevel(world: WorldManager, level: LevelBounds): void {
    if (!this.cells) return;
    const y0 = level.floorY + 1;
    const y1 = Math.max(y0, level.ceilingY - 1);

    for (let z = 0; z < this.mapD; z++) {
      for (let x = 0; x < this.mapW; x++) {
        let kind: CellKind = CellKind.Rock;
        let hasOpen = false;
        let hasOre = false;

        for (let y = y0; y <= y1; y++) {
          const t = world.getBlock(x, y, z);
          if (t === BlockType.ExitDoor) {
            kind = CellKind.Exit;
            hasOpen = true;
            break;
          }
          if (t === BlockType.Shop) {
            kind = CellKind.Shop;
            hasOpen = true;
            break;
          }
          if (t === BlockType.Air || t === BlockType.SurfacePortal) {
            hasOpen = true;
          } else if (t === BlockType.Gold || t === BlockType.Iron) {
            hasOre = true;
          }
        }

        if (kind === CellKind.Rock) {
          if (hasOpen) kind = CellKind.Tunnel;
          else if (hasOre) kind = CellKind.Ore;
        }

        this.cells[x + z * this.mapW] = kind;
      }
    }
  }

  /** @returns true if any new cell was revealed. */
  private revealAround(player: Vector3D, blockSize: number, radiusWorld: number): boolean {
    if (!this.revealed) return false;
    const vx = Math.floor(player.x / blockSize);
    const vz = Math.floor(player.z / blockSize);
    const r = Math.ceil(radiusWorld / blockSize);
    const r2 = r * r;
    let changed = false;
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dz * dz > r2) continue;
        const x = vx + dx;
        const z = vz + dz;
        if (x < 0 || z < 0 || x >= this.mapW || z >= this.mapD) continue;
        const i = x + z * this.mapW;
        if (this.revealed[i]) continue;
        this.revealed[i] = 1;
        changed = true;
      }
    }
    return changed;
  }

  private paintTerrain(): void {
    if (!this.cells || !this.revealed) return;
    const w = this.terrain.width;
    const h = this.terrain.height;
    const img = this.tctx.createImageData(w, h);
    const data = img.data;

    for (let z = 0; z < h; z++) {
      for (let x = 0; x < w; x++) {
        const i = x + z * w;
        const pi = i * 4;
        if (!this.revealed[i]) {
          data[pi] = 6;
          data[pi + 1] = 8;
          data[pi + 2] = 12;
          data[pi + 3] = 255;
          continue;
        }
        const c = cellColor(this.cells[i] as CellKind);
        data[pi] = c[0]!;
        data[pi + 1] = c[1]!;
        data[pi + 2] = c[2]!;
        data[pi + 3] = 255;
      }
    }

    this.tctx.putImageData(img, 0, 0);
  }

  private paintOverlay(world: WorldManager, frame: MinimapFrame): void {
    if (!this.revealed) return;
    const ctx = this.octx;
    const size = this.displaySize;
    ctx.clearRect(0, 0, size, size);

    const sx = size / Math.max(1, this.mapW);
    const sy = size / Math.max(1, this.mapD);
    const bs = world.blockSize;

    const toPx = (wx: number, wz: number): { x: number; y: number } => ({
      x: (wx / bs) * sx,
      y: (wz / bs) * sy,
    });

    const isRevealed = (wx: number, wz: number): boolean => {
      const cx = Math.floor(wx / bs);
      const cz = Math.floor(wz / bs);
      if (cx < 0 || cz < 0 || cx >= this.mapW || cz >= this.mapD) return false;
      return this.revealed![cx + cz * this.mapW] === 1;
    };

    for (const m of frame.markers) {
      if (!isRevealed(m.x, m.z)) continue;
      const q = toPx(m.x, m.z);

      if (m.kind === 'exit') this.drawDiamond(ctx, q.x, q.y, 6, '#ff9a4a');
      else if (m.kind === 'shop') this.drawDiamond(ctx, q.x, q.y, 5, '#ffe08a');
      else if (m.kind === 'entrance') this.drawDiamond(ctx, q.x, q.y, 4.5, '#7ec8ff');
      else if (m.kind === 'enemy') {
        ctx.fillStyle = '#e85d5d';
        ctx.beginPath();
        ctx.arc(q.x, q.y, 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.55)';
        ctx.lineWidth = 1;
        ctx.stroke();
      } else if (m.kind === 'remote') {
        ctx.fillStyle = '#5ec8ff';
        ctx.beginPath();
        ctx.arc(q.x, q.y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,0.65)';
        ctx.lineWidth = 1.25;
        ctx.stroke();
      }
    }

    const player = toPx(frame.player.x, frame.player.z);
    this.drawPlayerCursor(ctx, player.x, player.y, frame.yaw);

    ctx.strokeStyle = 'rgba(232,238,245,0.4)';
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, size - 2, size - 2);
  }

  private drawPlayerCursor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    yaw: number,
  ): void {
    const lookX = -Math.sin(yaw);
    const lookZ = -Math.cos(yaw);
    const angle = Math.atan2(lookX, -lookZ);

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);

    // Soft outer ring
    ctx.beginPath();
    ctx.arc(0, 0, 11, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,255,255,0.28)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Dark base disc — readable on bright tunnels
    ctx.beginPath();
    ctx.arc(0, 1, 7.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(8, 12, 18, 0.9)';
    ctx.fill();

    // Bold chevron
    ctx.beginPath();
    ctx.moveTo(0, -12);
    ctx.lineTo(8.5, 9);
    ctx.lineTo(0, 4);
    ctx.lineTo(-8.5, 9);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0,0,0,0.65)';
    ctx.shadowBlur = 3;
    ctx.shadowOffsetY = 1;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#15202c';
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Facing tip accent
    ctx.beginPath();
    ctx.arc(0, -9.5, 2.1, 0, Math.PI * 2);
    ctx.fillStyle = '#7ec8ff';
    ctx.fill();
    ctx.strokeStyle = '#0a1520';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.restore();
  }

  private drawDiamond(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
    color: string,
  ): void {
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r, y);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - r, y);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  private injectStyles(): void {
    if (document.getElementById('minimap-styles')) return;
    const style = document.createElement('style');
    style.id = 'minimap-styles';
    style.textContent = `
      #minimap {
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 12;
        pointer-events: none;
        width: ${this.displaySize}px;
        height: ${this.displaySize}px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
        background: #05080c;
        border: 1px solid rgba(232, 238, 245, 0.28);
      }
      #minimap .minimap-terrain,
      #minimap .minimap-overlay {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
      }
      #minimap .minimap-terrain {
        image-rendering: pixelated;
        image-rendering: crisp-edges;
      }
      #minimap .minimap-overlay {
        image-rendering: auto;
      }
      #minimap .minimap-label {
        position: absolute;
        left: 6px;
        bottom: 5px;
        font-size: 10px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: rgba(232, 238, 245, 0.85);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.9);
        z-index: 1;
      }
    `;
    document.head.appendChild(style);
  }
}

function cellColor(kind: CellKind): [number, number, number] {
  switch (kind) {
    case CellKind.Tunnel:
      return [178, 196, 214];
    case CellKind.Ore:
      return [130, 118, 72];
    case CellKind.Exit:
      return [255, 150, 70];
    case CellKind.Shop:
      return [255, 220, 130];
    case CellKind.Rock:
    default:
      return [26, 32, 40];
  }
}
